import {
  NOTIFICATION_TYPE,
  NOTIFICATION_ACTION,
  NOTIFICATION_MODAL,
  NOTIFICATIONS_PAGE_SIZE
} from './constants'
import { routes } from '../../router/routes'

/**
 * MOCK DATA — stands in for the `notificationsGet` response, which does not
 * exist yet. Served through the real slice/selector path by `useNotifications`,
 * so only this file and the NOTIFICATIONS_API_ENABLED branches go away when the
 * backend lands.
 *
 * Shapes mirror what we expect the API to return (the event contract itself is
 * documented in reducers/notifications/event-bus.js). Per field:
 * - `id`        stable string — used as the React key and the markRead payload.
 * - `type`      one of NOTIFICATION_TYPE — drives the item icon; unknown types
 *               fall back to the content icon, so the catalogue can grow.
 * - `title`     plain text, already localised by the server.
 * - `body`      plain text, optional.
 * - `createdAt` ISO timestamp — the item formats it at render time (never a
 *               pre-baked "2h" string — that would be wrong the moment the
 *               panel stayed open).
 * - `read`      boolean.
 * - `action`    what a click does (see NOTIFICATION_ACTION in constants.js):
 *               navigate carries the deep link in `to` (`/playlists/{id}`,
 *               `/skill-bites/{id}`, `/{locationId}/{staticUrl}`…), mindset
 *               opens the M:Coach agent, modal names a NOTIFICATION_MODAL.
 *               Optional — a notification without one just marks read.
 *
 * The seven live items are the seven in-app/email pairs prototyped in the email
 * lab (/email-lab), copy for copy, so panel, page and email tell one story.
 * Behind them sits aged filler, purely so "Load more" has something to page
 * through. Gating per tenant/permissions (NC-52: e.g. no /quizzes in B2B) is
 * the API's responsibility, not the panel's.
 *
 * `buildMockNotifications({ from, size })` answers in the paginated envelope
 * the API owes us — `{ items, total, unreadCount }`, the count being unread
 * across the whole history rather than the page.
 */

const MINUTE = 60 * 1000
const HOUR = 60 * MINUTE
const DAY = 24 * HOUR

const ago = ms => new Date(Date.now() - ms).toISOString()

/**
 * Dev-environment seed content. The API payload will carry real deep links;
 * these ids only exist so a click in mock mode lands on a detail page rather
 * than a list. They resolve on dev and nowhere else — change them here if a
 * demo runs against another environment.
 */
const DEV_SEED = {
  playlistId: 'pz4cppy'
}

const devPlaylistPath = `${routes.playlists.path}/${DEV_SEED.playlistId}`

const buildCurated = () => [
  {
    id: 'n-welcome',
    type: NOTIFICATION_TYPE.FEATURE,
    title: 'Welcome to Mindtools!',
    body: 'Take a two-minute tour of your three core tools.',
    createdAt: ago(5 * MINUTE),
    read: false,
    action: { kind: NOTIFICATION_ACTION.MODAL, modal: NOTIFICATION_MODAL.WELCOME_TOUR }
  },
  {
    id: 'n-quiz',
    type: NOTIFICATION_TYPE.CONTENT,
    title: 'Test your AI skills',
    body: 'A 5-minute quiz to see where you stand.',
    createdAt: ago(30 * MINUTE),
    read: false,
    action: { kind: NOTIFICATION_ACTION.NAVIGATE, to: routes.quizzes.path }
  },
  {
    id: 'n-mcoach',
    type: NOTIFICATION_TYPE.FEATURE,
    title: 'Try M:Coach',
    body: 'Not sure where to start? Tap a sample prompt and see.',
    createdAt: ago(2 * HOUR),
    read: false,
    action: { kind: NOTIFICATION_ACTION.MINDSET }
  },
  {
    id: 'n-skill-bite',
    type: NOTIFICATION_TYPE.CONTENT,
    title: 'New Skill Bite in Leadership',
    body: 'In a topic you follow',
    createdAt: ago(2 * HOUR),
    read: false,
    // No seed id for a single Skill Bite, so this lands on the list; the API
    // payload carries `/skill-bites/{id}`.
    action: { kind: NOTIFICATION_ACTION.NAVIGATE, to: routes['skill-bites'].path }
  },
  {
    id: 'n-playlist',
    type: NOTIFICATION_TYPE.CONTENT,
    title: 'Pick up where you left off',
    body: 'Leading with Confidence — 3 of 7 items done.',
    createdAt: ago(DAY),
    read: false,
    action: { kind: NOTIFICATION_ACTION.NAVIGATE, to: devPlaylistPath }
  },
  {
    id: 'n-org-recommends',
    type: NOTIFICATION_TYPE.CONTENT,
    title: 'Your organisation recommends',
    body: 'Three resources picked for your team this month.',
    createdAt: ago(3 * DAY),
    read: true,
    action: { kind: NOTIFICATION_ACTION.MODAL, modal: NOTIFICATION_MODAL.ORG_RECOMMENDS }
  },
  {
    id: 'n-whats-new',
    type: NOTIFICATION_TYPE.FEATURE,
    title: "What's new in Mindtools",
    body: 'Improvements you asked for — quieter, faster, clearer.',
    createdAt: ago(5 * DAY),
    read: true,
    action: { kind: NOTIFICATION_ACTION.MODAL, modal: NOTIFICATION_MODAL.WHATS_NEW }
  }
]

/**
 * Older history: the same seven, aged and already read, so the full page has
 * more than one page to fetch. Nothing here is meant to be read as copy — it
 * exists to exercise "Load more", the "Earlier" group and the unread count
 * being bigger than the loaded page.
 */
const HISTORY_LENGTH = 16
const HISTORY_START_DAYS = 8
const HISTORY_STEP_DAYS = 3

const buildHistory = curated =>
  Array.from({ length: HISTORY_LENGTH }, (unused, index) => ({
    ...curated[index % curated.length],
    id: `n-history-${index}`,
    createdAt: ago((HISTORY_START_DAYS + index * HISTORY_STEP_DAYS) * DAY),
    read: true
  }))

export const buildMockNotifications = ({
  from = 0,
  size = NOTIFICATIONS_PAGE_SIZE,
  preferences = {}
} = {}) => {
  // Rebuilt per call so `createdAt` stays relative to now.
  const curated = buildCurated()
  // Standing in for the server: a switched-off type is never sent, so it never
  // reaches the client's totals either (NC-40, and NC-52 for the same reason —
  // filtering a real payload client-side would be the wrong place for it).
  // Types outside the preference catalogue are always sent.
  const all = curated
    .concat(buildHistory(curated))
    .filter(item => preferences[item.type] !== false)

  return {
    items: all.slice(from, from + size),
    total: all.length,
    // Across the whole history, not the page — the distinction the badge lives
    // or dies by once pagination is real (NC-50).
    unreadCount: all.filter(item => !item.read).length
  }
}

export { NOTIFICATION_TYPE }
